'use client';

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { LazyImage, SmartIcon } from '@/shared/blocks/common';
import { BorderBeam } from '@/shared/components/magicui/border-beam';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/components/ui/accordion';
import { ScrollAnimation } from '@/shared/components/ui/scroll-animation';
import { cn } from '@/shared/lib/utils';
import { Section } from '@/shared/types/blocks/landing';

// CDN base URL for images (Cloudflare R2 with custom domain)
const CDN_BASE = 'https://r2.zestygen.com';

// Background images for subtle image wall effect - served from CDN
const BG_IMAGES = [
  { src: `${CDN_BASE}/hero/1.webp`, alt: 'AI Style Art' },
  { src: `${CDN_BASE}/hero/2.webp`, alt: 'AI Filter' },
  { src: `${CDN_BASE}/hero/3.webp`, alt: 'AI Pixar 3D' },
  { src: `${CDN_BASE}/hero/4.webp`, alt: 'AI Art Effect' },
  { src: `${CDN_BASE}/hero/5.webp`, alt: 'AI Pixel Art' },
  { src: `${CDN_BASE}/hero/6.webp`, alt: 'AI Art Generator' },
  { src: `${CDN_BASE}/hero/7.webp`, alt: 'AI Video' },
  { src: `${CDN_BASE}/hero/8.webp`, alt: 'AI Landscape' },
  { src: `${CDN_BASE}/hero/9.webp`, alt: 'AI Pet' },
];

// Subtle image wall background component
function SubtleImageWall() {
  const row1 = [...BG_IMAGES, ...BG_IMAGES];
  const row2 = [...BG_IMAGES.slice(4), ...BG_IMAGES.slice(0, 4), ...BG_IMAGES];
  
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden">
      {/* Very subtle overlay - 90% coverage for hint of images */}
      <div className="absolute inset-0 z-20 bg-gradient-to-b from-background/90 via-background/85 to-background/95" />
      
      {/* Image rows */}
      <div 
        className="absolute -inset-[20%] z-10 flex flex-col justify-center gap-2 opacity-60"
        style={{ transform: 'rotate(-8deg)' }}
      >
        <motion.div
          className="flex gap-2"
          animate={{ x: [0, -1200] }}
          transition={{ x: { duration: 40, repeat: Infinity, ease: 'linear' } }}
        >
          {row1.map((image, idx) => (
            <div key={`r1-${idx}`} className="relative w-[180px] h-[180px] flex-shrink-0 overflow-hidden rounded-lg">
              <img src={image.src} alt={image.alt} className="h-full w-full object-cover" loading="lazy" />
            </div>
          ))}
        </motion.div>
        
        <motion.div
          className="flex gap-2"
          animate={{ x: [-1200, 0] }}
          transition={{ x: { duration: 50, repeat: Infinity, ease: 'linear' } }}
        >
          {row2.map((image, idx) => (
            <div key={`r2-${idx}`} className="relative w-[180px] h-[180px] flex-shrink-0 overflow-hidden rounded-lg">
              <img src={image.src} alt={image.alt} className="h-full w-full object-cover" loading="lazy" />
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export function FeaturesAccordion({
  section,
  className,
}: {
  section: Section;
  className?: string;
}) {
  const [activeItem, setActiveItem] = useState<string>('item-1');
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const images: any = {};
  section.items?.forEach((item, idx) => {
    images[`item-${idx + 1}`] = {
      image: item.image?.src ?? '',
      alt: item.image?.alt || item.title || '',
    };
  });

  return (
    // overflow-x-hidden to prevent horizontal scroll
    <section
      className={cn(
        'relative overflow-x-hidden py-16 md:py-24',
        section.className,
        className
      )}
    >
      {/* Optional subtle image wall background */}
      {section.show_image_wall && <SubtleImageWall />}
      
      <div className="absolute inset-0 -z-10 bg-linear-to-b sm:inset-6 sm:rounded-b-3xl dark:block dark:to-[color-mix(in_oklab,var(--color-zinc-900)_75%,var(--color-background))]"></div>
      {/* add overflow-x-hidden to container */}
      <div className="container space-y-8 overflow-x-hidden px-2 sm:px-6 md:space-y-16 lg:space-y-20 dark:[--color-border:color-mix(in_oklab,var(--color-white)_10%,transparent)]">
        <ScrollAnimation>
          <div className="mx-auto max-w-4xl text-center text-balance">
            <h2 className="text-foreground mb-4 text-3xl font-semibold tracking-tight md:text-4xl">
              {section.title}
            </h2>
            <p className="text-muted-foreground mb-6 md:mb-12 lg:mb-16">
              {section.description}
            </p>
          </div>
        </ScrollAnimation>

        {/* grid: clamp min-w-0 and fix px padding/breakpoints */}
        <div className="grid min-w-0 gap-12 sm:px-6 md:grid-cols-2 lg:gap-20 lg:px-0">
          <ScrollAnimation delay={0.1} direction="left">
            {isMounted ? (
              <Accordion
                type="single"
                value={activeItem}
                onValueChange={(value) => setActiveItem(value as string)}
                className="w-full"
              >
                {section.items?.map((item, idx) => (
                  <AccordionItem value={`item-${idx + 1}`} key={idx}>
                    <AccordionTrigger>
                      <div className="flex items-center gap-2 text-base">
                        {item.icon && (
                          <SmartIcon name={item.icon as string} size={24} />
                        )}
                        {item.title}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>{item.description}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="w-full space-y-4">
                {section.items?.map((item, idx) => (
                  <div key={idx} className="border-b py-4">
                    <div className="flex items-center gap-2 text-base font-medium">
                      {item.icon && (
                        <SmartIcon name={item.icon as string} size={24} />
                      )}
                      {item.title}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollAnimation>

          <ScrollAnimation delay={0.2} direction="right">
            {/* min-w-0/flex-shrink to prevent overflow */}
            <div className="bg-background relative flex min-w-0 flex-shrink overflow-hidden rounded-3xl border p-2">
              <div className="absolute inset-0 right-0 ml-auto w-15 border-l bg-[repeating-linear-gradient(-45deg,var(--color-border),var(--color-border)_1px,transparent_1px,transparent_8px)]"></div>
              <div className="bg-background relative aspect-76/59 w-full min-w-0 rounded-2xl sm:w-[calc(3/4*100%+3rem)]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${activeItem}-id`}
                    initial={{ opacity: 0, y: 6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="size-full overflow-hidden rounded-2xl border shadow-md"
                  >
                    <LazyImage
                      src={images[activeItem].image}
                      className="size-full object-cover object-left-top dark:mix-blend-lighten"
                      alt={images[activeItem].alt}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
              <BorderBeam
                duration={6}
                size={200}
                className="from-transparent via-yellow-700 to-transparent dark:via-white/50"
              />
            </div>
          </ScrollAnimation>
        </div>
      </div>
    </section>
  );
}
